/**
 * "Copy link" (23 Oct): share a ticket in Teams or email in one click.
 *
 * IT and admins get "Copy link ▾" with both links: the ITSM link (opens the
 * record in the Ivanti console, for colleagues) and the HelpDesk link (for
 * the requester). Everyone else gets a plain "Copy link" for the HelpDesk
 * link. The clipboard gets a titled link ("Incident 1032 – Ethernet …") as
 * HTML and the URL as plain text; see `lib/ticket-link.ts`.
 */

import { ChevronDown, Icon, Link as LinkIcon } from "@norican/icons";
import { Button, Menu, useToastManager } from "@norican/react";

import { useT } from "@/i18n";
import { useMe } from "@/lib/api";
import { logger } from "@/lib/logger";
import { copyLink, copyLinkOptions, type LinkOption, linkTitle } from "@/lib/ticket-link";

export interface CopyLinkMenuProps {
  /** `INCIDENT` or `SERVICE_REQUEST`. */
  kind: string;
  number: string;
  /** The ticket's subject, shortened into the pasted title. */
  summary?: string | null;
  /** A console link the server already built (Ticket search rows). */
  itsmUrl?: string | null;
  /** Offer the HelpDesk link too (default true). */
  helpdesk?: boolean;
  /** `button`: "Copy link" with text; `icon`: a compact icon button (table rows). */
  variant?: "button" | "icon";
}

export function CopyLinkMenu({
  kind,
  number,
  summary,
  itsmUrl,
  helpdesk = true,
  variant = "button",
}: CopyLinkMenuProps) {
  const { t } = useT();
  const { data: me } = useMe();
  const toast = useToastManager();
  const options = copyLinkOptions({
    kind,
    number,
    origin: typeof window === "undefined" ? null : window.location.origin,
    itsmBase: me?.kiosk != null ? null : me?.itsm_url,
    itsmUrl,
    helpdesk,
  });
  if (options.length === 0) {
    return null;
  }

  const reference =
    kind === "SERVICE_REQUEST"
      ? t("copyLink.reference.SERVICE_REQUEST", { number })
      : kind === "INCIDENT"
        ? t("copyLink.reference.INCIDENT", { number })
        : t("copyLink.reference.UNKNOWN", { number });
  const title = linkTitle(reference, summary);

  const copy = (option: LinkOption) => {
    copyLink(title, option.url).then(
      () => toast.add({ title: t("copyLink.copied"), type: "success" }),
      (error: unknown) => {
        logger.debug("[copy-link] clipboard refused", error);
        toast.add({ title: t("copyLink.failed"), description: option.url, type: "danger" });
      },
    );
  };
  const optionLabel = (option: LinkOption) =>
    option.target === "itsm" ? t("copyLink.itsm") : t("copyLink.helpdesk");
  const label = t("copyLink.label");
  const ariaLabel = t("copyLink.ariaLabel", { reference });

  const [only] = options;
  if (options.length === 1 && only !== undefined) {
    const single = `${ariaLabel}: ${optionLabel(only)}`;
    return variant === "icon" ? (
      <Button
        variant="ghost"
        tone="neutral"
        size="sm"
        iconOnly
        aria-label={single}
        title={single}
        onClick={() => copy(only)}
      >
        <Icon icon={LinkIcon} size="sm" />
      </Button>
    ) : (
      <Button
        variant="outline"
        tone="neutral"
        startIcon={<Icon icon={LinkIcon} size="sm" />}
        aria-label={single}
        onClick={() => copy(only)}
      >
        {label}
      </Button>
    );
  }

  return (
    <Menu.Root>
      <Menu.Trigger
        render={
          variant === "icon" ? (
            <Button
              variant="ghost"
              tone="neutral"
              size="sm"
              iconOnly
              aria-label={ariaLabel}
              title={ariaLabel}
            />
          ) : (
            <Button
              variant="outline"
              tone="neutral"
              startIcon={<Icon icon={LinkIcon} size="sm" />}
              endIcon={<Icon icon={ChevronDown} size="sm" />}
              aria-label={ariaLabel}
            />
          )
        }
      >
        {variant === "icon" ? <Icon icon={LinkIcon} size="sm" /> : label}
      </Menu.Trigger>
      <Menu.Portal>
        <Menu.Positioner sideOffset={6} align="end">
          <Menu.Popup width="content">
            {options.map((option) => (
              <Menu.Item key={option.target} onClick={() => copy(option)}>
                <span className="flex flex-col">
                  <span className="font-semibold">{optionLabel(option)}</span>
                  <span className="text-fg-muted text-xs">
                    {option.target === "itsm" ? t("copyLink.itsmHint") : t("copyLink.helpdeskHint")}
                  </span>
                </span>
              </Menu.Item>
            ))}
          </Menu.Popup>
        </Menu.Positioner>
      </Menu.Portal>
    </Menu.Root>
  );
}
